import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, LinkProps, NonCancelableCustomEvent, Table, TableProps } from '@amzn/awsui-components-react';
import { useNavigate } from 'react-router-dom';
import BoardItem from '@amzn/awsui-board-components/board-item';
import { I18nFormatter } from 'modules/core/class';
import { Card, CardBaseProps, CardHeader, i18nStrings } from './Card';
import { awsRumService } from 'rum-service';
import { DefectContributor, useCards } from './hooks/useCards.hook';
import { logger } from 'modules/core';

interface PoLineCountProps {
    count: number;
    account: string;
    costCenter: string;
}

export const TopAccounts = ({ onPanelChange }: CardBaseProps) => {
    const { t } = useTranslation('home');
    const { useDefectContributors } = useCards();
    const { data, isPending, error } = useDefectContributors(20);

    useEffect(() => {
        if (error) {
            logger.error(error);
        }
    }, [error]);

    const formatPoOwnersCount = useCallback((count: number, ownerType: string): string => {
        if (count >= 10) return `At least 10 ${ownerType}`;
        return count === 1 ? `${count} ${ownerType.slice(0, -1)}` : `${count} ${ownerType}`;
    }, []);

    const getFormattedDateRange = useCallback(
        (item: DefectContributor) => {
            return `${I18nFormatter.date(t, item.earliestPoCreationDate)} - 
                ${I18nFormatter.date(t, item.latestPoCreationDate)}`;
        },
        [t]
    );

    const PoLineCount = ({ count, account, costCenter }: PoLineCountProps) => {
        const navigate = useNavigate();
        const url = `/receiving?glAccount=${account}&costCenter=${costCenter}&statuses=no_receiving&invoiceStatuses=partially_invoiced|||fully_invoiced`;

        const handleClick = () => {
            awsRumService.logHomeItemClick(Card.TopAccounts);
        };

        const handleFollow = (event: CustomEvent<LinkProps.FollowDetail>) => {
            event.preventDefault();
            awsRumService.logHomeItemClick(Card.TopAccounts);
            navigate(url);
        };

        return (
            <Link variant="secondary" href={url} onFollow={handleFollow} onClick={handleClick}>
                {count}
            </Link>
        );
    };

    const columnDefinitions = useMemo(
        () => [
            {
                id: 'lineCount',
                header: 'Number of PO lines',
                cell: (item: DefectContributor) => (
                    <PoLineCount count={item.lineCount} account={item.account} costCenter={item.costCenter} />
                ),
                sortingField: 'lineCount'
            },
            {
                id: 'account',
                header: 'GL account',
                cell: (item: DefectContributor) => item.account
            },
            {
                id: 'costCenter',
                header: 'Cost center',
                cell: (item: DefectContributor) => item.costCenter
            },
            {
                id: 'team',
                header: 'Business',
                cell: (item: DefectContributor) => item.team.replaceAll('-', ' '),
                sortingField: 'team'
            },
            {
                id: 'totalAmount',
                header: 'Total amount',
                cell: (item: DefectContributor) => I18nFormatter.amountFixed2Decimals(t, item.totalAmount)
            },
            {
                id: 'currency',
                header: 'Currency',
                cell: (item: DefectContributor) => item.currency
            },
            {
                id: 'requesters',
                header: 'PO requesters',
                cell: (item: DefectContributor) => formatPoOwnersCount(item.topRequesters.length, 'requesters')
            },
            {
                id: 'creators',
                header: 'PO creators',
                cell: (item: DefectContributor) => formatPoOwnersCount(item.topCreators.length, 'creators')
            },
            {
                id: 'poDateRange',
                header: 'PO creation date range',
                cell: (item: DefectContributor) => getFormattedDateRange(item),
                sortingField: 'earliestPoCreationDate'
            }
        ],
        [t, formatPoOwnersCount, getFormattedDateRange]
    );

    const [sortingColumn, setSortingColumn] = useState<TableProps.SortingColumn<DefectContributor>>({
        sortingField: 'lineCount'
    });
    const [sortingDescending, setSortingDescending] = useState(true);

    const getSortValue = useCallback((item: DefectContributor, field: string): number | string => {
        switch (field) {
            case 'lineCount':
                return item.lineCount;
            case 'team':
                return item.team;
            case 'earliestPoCreationDate':
                return item.earliestPoCreationDate;
            default:
                return 0;
        }
    }, []);

    const sortedItems = useMemo(() => {
        if (!data || !sortingColumn.sortingField) return data ?? [];

        const itemsWithSortKeys = data.map(item => ({
            item,
            sortKey: getSortValue(item, sortingColumn.sortingField!)
        }));

        itemsWithSortKeys.sort((a, b) => {
            if (a.sortKey < b.sortKey) return sortingDescending ? 1 : -1;
            if (a.sortKey > b.sortKey) return sortingDescending ? -1 : 1;
            return 0;
        });

        return itemsWithSortKeys.map(({ item }) => item);
    }, [data, sortingColumn.sortingField, sortingDescending, getSortValue]);

    const handleSortingChange = useCallback(
        (event: NonCancelableCustomEvent<TableProps.SortingState<DefectContributor>>) => {
            const { sortingColumn: newSortingColumn, isDescending = true } = event.detail;
            setSortingColumn(newSortingColumn);
            setSortingDescending(isDescending);
        },
        []
    );

    return (
        <BoardItem
            i18nStrings={i18nStrings}
            header={
                <CardHeader dataHeaderId={Card.TopAccounts} onPanelChange={onPanelChange}>
                    Top defect contributors
                </CardHeader>
            }
        >
            <Table
                columnDefinitions={columnDefinitions}
                items={sortedItems}
                loading={isPending || error !== null}
                loadingText="Loading defect contributors"
                empty={<p>No defect contributors found</p>}
                variant="borderless"
                resizableColumns
                stickyHeader
                sortingColumn={sortingColumn}
                sortingDescending={sortingDescending}
                onSortingChange={handleSortingChange}
            />
        </BoardItem>
    );
};
